import { TConsumerItem } from '../../../types/data';
import { DEFAULT_PHONE_MASK, PHONE_CUSTOM_MASK } from './masks';

export const convertItemsToListSelect = (items?: Array<TConsumerItem>, currentIso?: string): Array<TConsumerItem> =>
    Array.isArray(items) ? items?.map?.((item) => convertItemToListSelect(item, currentIso) as TConsumerItem) : [];
const convertItemToListSelect = (item: TConsumerItem, currentIso?: string): TConsumerItem => {
    return {
        ...item,
        icon: item.countryIso,
        selected: item.countryIso?.toLowerCase?.() === currentIso?.toLowerCase?.(),
    };
};

/**
 * A phone `maxLength` (item- or options-level) counts digits including the country code, without the `+`,
 * while the input itself holds the masked value (`+44 7911 123-456`). Converts that digit budget into the
 * length of the formatted string, so the native `maxLength` attribute cuts the input at the right place.
 *
 * react-phone-input-2 builds its format as `+` + dial code + a space + the country mask, so the same layout
 * is reproduced here from PHONE_CUSTOM_MASK (with the default mask the input is given as a fallback).
 *
 * @param digitsLimit - allowed number of digits, country code included
 * @param iso2 - currently selected country, picks the mask
 * @param dialCode - country code digits of the selected item (no `+`)
 * @returns length of the formatted value, or `undefined` when there is no limit
 */
export const getFormattedMaxLength = (digitsLimit?: number, iso2?: string, dialCode = ''): number | undefined => {
    if (!digitsLimit) {
        return undefined;
    }
    // No country resolved: the input shows `+` followed by plain digits.
    if (!dialCode) {
        return digitsLimit + 1;
    }
    const nationalDigitsRaw = digitsLimit - dialCode.length;
    if (nationalDigitsRaw <= 0) {
        return digitsLimit + 1;
    }
    const mask = (PHONE_CUSTOM_MASK as Record<string, string | undefined>)[iso2?.toLowerCase?.() ?? ''] ?? DEFAULT_PHONE_MASK;
    let maskDigitsCapacity = 0;
    for (const char of mask) {
        if (char === '.') {
            maskDigitsCapacity += 1;
        }
    }
    const nationalDigits = Math.min(nationalDigitsRaw, maskDigitsCapacity);
    let digits = 0;
    let maskLength = 0;
    for (const char of mask) {
        maskLength += 1;
        if (char === '.') {
            digits += 1;
            if (digits === nationalDigits) {
                break;
            }
        }
    }
    // A limit longer than the mask: capped at the mask's digit capacity.
    const tail = nationalDigits - digits;
    const processedMask = mask.slice(0, maskLength);
    const hasUnclosedBracket = processedMask.includes('(') && !processedMask.includes(')');
    const extraForUnclosedBracket = hasUnclosedBracket ? 1 : 0;
    // `+`, the dial code, the space after it, the mask part length, and potential bracket adjustment.
    return 1 + dialCode.length + 1 + maskLength + tail + extraForUnclosedBracket;
};
